import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getCurrentTeamId, setCurrentTeamId as persistCurrentTeamId } from './currentTeamStore'

export type Team = {
  id: string
  name: string
}

// The real teams this signed-in account actually supervises
// (teams.supervisor_id, teams_read_all letting any authenticated account
// read the table), what every supervisor page's team switcher lists.
export async function getSupervisorTeams(userId: string): Promise<Team[]> {
  const { data, error } = await supabase
    .from('teams')
    .select('id, name')
    .eq('supervisor_id', userId)
    .order('name', { ascending: true })
  if (error || !data) return []
  return data as Team[]
}

function sameTeams(a: Team[], b: Team[]): boolean {
  return a.length === b.length && a.every((team, index) => team.id === b[index].id)
}

/**
 * The signed-in supervisor's own real teams. Starts empty, with no team
 * selected, until the read resolves: a page renders no team-scoped content
 * before then, rather than another account's. Bails out of both updates once
 * the fetch resolves to exactly what is already showing (teams) or already
 * selected (currentTeamId), rather than setting state unconditionally: a
 * switchTeam call fired before this same mount's own fetch resolves must
 * never be clobbered by it landing late. Shared across every supervisor
 * page's own TeamSwitcher, the same reason useProfileIdentity.ts is shared
 * across pages.
 */
export function useSupervisorTeams(userId: string | null): {
  teams: Team[]
  currentTeamId: string
  // False until the read for this account has resolved (an account with no
  // teams resolves to an empty list), so a page can hold its team-scoped
  // content back rather than render it against no team.
  teamsLoaded: boolean
  switchTeam: (teamId: string) => void
} {
  const [teams, setTeams] = useState<Team[]>([])
  const [currentTeamId, setCurrentTeamIdState] = useState<string>('')
  const [teamsLoaded, setTeamsLoaded] = useState(false)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    getSupervisorTeams(userId).then((fetched) => {
      if (cancelled) return
      setTeamsLoaded(true)
      if (fetched.length === 0) return
      setTeams((current) => (sameTeams(current, fetched) ? current : fetched))
      setCurrentTeamIdState((current) => {
        if (fetched.some((team) => team.id === current)) return current
        const persisted = getCurrentTeamId(userId)
        return fetched.some((team) => team.id === persisted) ? persisted : fetched[0].id
      })
    })
    return () => {
      cancelled = true
    }
  }, [userId])

  function switchTeam(nextTeamId: string) {
    if (nextTeamId === currentTeamId) return
    if (userId) persistCurrentTeamId(userId, nextTeamId)
    setCurrentTeamIdState(nextTeamId)
  }

  return { teams, currentTeamId, teamsLoaded, switchTeam }
}
